import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Save, Plus, Trash2, Calculator, ArrowLeft, Send, UserCheck } from 'lucide-react';

// Interfaces
interface CostItem {
    id: number;
    description: string;
    cost: number;
    isRecurring: boolean;
}

interface PresetItem {
    label: string;
    value?: string;
    desc?: string;
    cost?: number;
}

interface UserRequest {
    id: number;
    name: string;
    companyName: string;
    email: string;
    serviceType: string;
    projectBrief?: string;
}

const CreateQuotation: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isSending, setIsSending] = useState(false);
    
    // --- PENDING REQUESTS LIST ---
    const [pendingRequests, setPendingRequests] = useState<UserRequest[]>([]);
    
    // --- SELECTED CLIENT STATE ---
    const [selectedUserId, setSelectedUserId] = useState<number | string>("");
    const [selectedUser, setSelectedUser] = useState<UserRequest | null>(null);
    const [serviceType, setServiceType] = useState(''); // Default Empty, will fill on select

    // --- QUOTATION FORM ---
    const [projectTitle, setProjectTitle] = useState('');
    const [projectDescription, setProjectDescription] = useState('');
    
    // Costing
    const [devCost, setDevCost] = useState<number>(0);
    const [maintenanceCost, setMaintenanceCost] = useState<number>(0);

    // Client Liabilities
    const [clientCosts, setClientCosts] = useState<CostItem[]>([]);

    // --- PRESETS ---
    const presets: { [key: string]: PresetItem[] } = {
        'Web Development': [
            { label: "Select Preset...", value: "" },
            { label: "Static Business Website", desc: "5 Pages, Contact Form, SEO Basic", cost: 15000 },
            { label: "E-Commerce Website", desc: "Product Listing, Cart, Payment Gateway, Admin Panel", cost: 45000 },
        ],
        'App Development': [
            { label: "Select Preset...", value: "" },
            { label: "Hybrid App (Flutter)", desc: "Android + iOS, Basic UI, API Integration", cost: 60000 },
            { label: "E-Commerce App", desc: "Full featured shopping app with push notifications", cost: 120000 },
        ]
    };

    // --- 1. FETCH PENDING REQUESTS ON LOAD ---
    useEffect(() => {
        // fetch('http://localhost:2025/api/quotation/requests')
        fetch('https://www.projectinfotech.in/api/quotation/requests')
            .then(res => res.json())
            .then(data => {
                setPendingRequests(data);
                
                // --- 2. CHECK IF NAVIGATED FROM REQUEST PAGE ---
                if (location.state && location.state.user) {
                    const u = location.state.user;
                    handleClientSelection(u.id, data); // Auto select user
                }
            })
            .catch(err => console.error("Error fetching users:", err));
    }, []);

    // --- HANDLE CLIENT SELECTION ---
    const handleClientSelection = (id: number | string, list = pendingRequests) => {
        const idNum = Number(id);
        setSelectedUserId(idNum);
        
        const user = list.find(u => u.id === idNum);
        if (user) {
            setSelectedUser(user);
            setServiceType(user.serviceType); // AUTO-SET SERVICE TYPE
            setProjectDescription(user.projectBrief || ""); // Pre-fill their brief
            
            // Auto-set default liabilities based on service
            if(user.serviceType === 'App Development') {
                setClientCosts([
                    { id: 1, description: "Google Play Console (One Time)", cost: 2500, isRecurring: false },
                    { id: 2, description: "OTP Service (SMS API)", cost: 2000, isRecurring: true },
                ]);
            } else {
                setClientCosts([
                    { id: 1, description: "Domain Name (.com)", cost: 900, isRecurring: true },
                    { id: 2, description: "Server Hosting (AWS/VPS)", cost: 5000, isRecurring: true },
                ]);
            }
        }
    };

    // --- HANDLERS ---
    const handleAddClientCost = () => {
        setClientCosts([...clientCosts, { id: Date.now(), description: "", cost: 0, isRecurring: true }]);
    };

    const handleRemoveClientCost = (id: number) => {
        setClientCosts(clientCosts.filter(c => c.id !== id));
    };

    const handleClientCostChange = (id: number, field: string, value: any) => {
        setClientCosts(clientCosts.map(c => c.id === id ? { ...c, [field]: value } : c));
    };

    const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        // Safe check for presets based on current service type
        const list = presets[serviceType] || [];
        const selected = list.find(p => p.label === e.target.value);
        
        if (selected && selected.desc !== undefined && selected.cost !== undefined) {
            setProjectTitle(selected.label);
            setProjectDescription(prev => selected.desc + "\n\nClient Request Note: " + prev); // Append to existing brief
            setDevCost(selected.cost);
        }
    };

    // --- GENERATE & SEND ---
    const handleGenerateAndSend = async () => {
        if(!selectedUser) return alert("Please select a client first!");
        setIsSending(true);

        const totalClientLiability = clientCosts.reduce((acc, curr) => acc + Number(curr.cost), 0);
        const grandTotal = Number(devCost) + totalClientLiability;

        const payload = {
            quotationId: selectedUser.id,
            userEmail: selectedUser.email,
            userName: selectedUser.name,
            projectTitle,
            projectDescription,
            serviceType,
            devCost,
            maintenanceCost,
            clientCosts,
            totalAmount: grandTotal
        };

        try {
            // const response = await fetch('http://localhost:2025/api/quotation/generate', {
            const response = await fetch('https://www.projectinfotech.in/api/quotation/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                alert("Quotation generated and sent successfully!");
                navigate('/admin/quotation/history');
            } else {
                alert("Failed to send quotation.");
            }
        } catch (error) {
            console.error("Error:", error);
            alert("Server error.");
        } finally {
            setIsSending(false);
        }
    };

    const totalClientLiability = clientCosts.reduce((acc, curr) => acc + Number(curr.cost), 0);
    const grandTotal = Number(devCost) + totalClientLiability;

    return (
        <div className="p-6 bg-gray-50 min-h-screen font-sans">
            <div className="flex items-center justify-between mb-6">
                <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 font-bold">
                    <ArrowLeft size={20} className="mr-2"/> Back
                </button>
                <div className="flex gap-3">
                    <button 
                        onClick={handleGenerateAndSend} 
                        disabled={isSending}
                        className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 shadow-lg disabled:bg-gray-400"
                    >
                        {isSending ? 'Generating PDF...' : <><Send size={18}/> Generate & Send Quote</>}
                    </button>
                </div>
            </div>

            {/* --- CLIENT SELECTION DROPDOWN --- */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-red-100 mb-8">
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Select Client for Quotation</label>
                <div className="relative">
                    <select 
                        value={selectedUserId}
                        onChange={(e) => handleClientSelection(e.target.value)}
                        className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 appearance-none bg-gray-50 text-gray-800 font-medium"
                    >
                        <option value="">-- Select a Pending Request --</option>
                        {pendingRequests.map(req => (
                            <option key={req.id} value={req.id}>
                                {req.name} ({req.companyName || 'Indiv.'}) - Requested: {req.serviceType}
                            </option>
                        ))}
                    </select>
                    <UserCheck className="absolute left-3 top-3.5 text-gray-500" size={20}/>
                </div>
            </div>

            {selectedUser ? (
                <div className="grid lg:grid-cols-3 gap-8 animate-fade-in">
                    {/* Editor Column */}
                    <div className="lg:col-span-2 space-y-6">
                        
                        {/* Auto-Filled Details */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Client & Service Details (Auto-Filled)</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-gray-500 font-bold uppercase">Client Name</label>
                                    <input type="text" value={selectedUser.name} readOnly className="w-full p-2 bg-gray-100 border rounded font-bold text-gray-700" />
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 font-bold uppercase">Service Type</label>
                                    <input type="text" value={serviceType} readOnly className={`w-full p-2 border rounded font-bold ${serviceType.includes('App') ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`} />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <div className="flex justify-between items-center mb-4 border-b pb-2">
                                <h3 className="text-lg font-bold text-gray-800">Scope of Work</h3>
                                {/* Dynamic Presets based on Service Type */}
                                <select onChange={handlePresetChange} className="text-sm border rounded p-1 text-gray-600">
                                    <option>Select Quick Preset...</option>
                                    {(presets[serviceType] || []).map((p,i) => (
                                        p.value !== "" && <option key={i}>{p.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-4">
                                <input type="text" value={projectTitle} onChange={e => setProjectTitle(e.target.value)} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none" placeholder="Project Title" />
                                <textarea rows={6} value={projectDescription} onChange={e => setProjectDescription(e.target.value)} className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none" placeholder="Description..."></textarea>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                             <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-gray-800">Client Liabilities (3rd Party Costs)</h3>
                                <button onClick={handleAddClientCost} className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-full font-bold hover:bg-blue-100 flex items-center"><Plus size={14} className="mr-1"/> Add</button>
                            </div>
                            <div className="space-y-3">
                                {clientCosts.map((item, index) => (
                                    <div key={item.id} className="flex gap-2 items-center">
                                        <span className="text-gray-400 font-mono text-sm">{index + 1}.</span>
                                        <input type="text" placeholder="Item Name" value={item.description} onChange={(e) => handleClientCostChange(item.id, 'description', e.target.value)} className="flex-1 p-2 border rounded text-sm"/>
                                        <div className="relative w-32">
                                            <span className="absolute left-2 top-2 text-gray-500 text-sm">₹</span>
                                            <input type="number" value={item.cost} onChange={(e) => handleClientCostChange(item.id, 'cost', e.target.value)} className="w-full pl-6 p-2 border rounded text-sm font-bold text-gray-700"/>
                                        </div>
                                        <button onClick={() => handleRemoveClientCost(item.id)} className="text-red-400 hover:text-red-600"><Trash2 size={18}/></button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Summary Column */}
                    <div className="lg:col-span-1">
                        <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-red-600 sticky top-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"><Calculator size={24} className="text-red-600"/> Cost Estimate</h3>
                            <div className="mb-4">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Dev Fee</label>
                                <input type="number" value={devCost} onChange={e => setDevCost(Number(e.target.value))} className="w-full p-2 border rounded font-bold text-gray-800"/>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-bold text-gray-700 mb-1">AMC</label>
                                <input type="number" value={maintenanceCost} onChange={e => setMaintenanceCost(Number(e.target.value))} className="w-full p-2 border rounded font-bold text-gray-700"/>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg space-y-2 border border-gray-200">
                                 <div className="flex justify-between text-sm font-bold text-red-600 border-t pt-2">
                                    <span>Total Est.</span>
                                    <span>₹ {grandTotal.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center py-20 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl">
                    <UserCheck size={48} className="mx-auto text-gray-300 mb-4"/>
                    <p className="text-gray-500 font-medium">Please select a client from the dropdown above to start creating a quotation.</p>
                </div>
            )}
        </div>
    );
};

export default CreateQuotation;